import React from 'react';
import { WorkflowRule } from '../../../../types/workflowRules';
import { AlertCircle } from 'lucide-react';

interface WizardStep1Props {
  formData: Partial<WorkflowRule>;
  updateFormData: (updates: Partial<WorkflowRule>) => void;
  onNext: () => void;
  onBack: () => void;
}

const AVAILABLE_OBJECTS = [
  'Client',
  'Lead',
  'Contact',
  'Opportunity',
  'Case',
  'Task',
  'Event',
  'Report'
];

export const WizardStep1BasicInfo: React.FC<WizardStep1Props> = ({
  formData,
  updateFormData,
  onNext
}) => {
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Rule name is required';
    }

    if (!formData.objectApiName) {
      newErrors.objectApiName = 'Please select an object';
    }

    if (!formData.triggerType) {
      newErrors.triggerType = 'Please select when this rule should run';
    }

    console.log('🔍 Step 1 Validation:', {
      formData,
      errors: newErrors,
      isValid: Object.keys(newErrors).length === 0
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    console.log('🎯 Step 1: handleNext called');
    console.log('Current form data:', formData);

    const isValid = validate();
    console.log('Validation result:', isValid);
    console.log('Errors:', errors);

    if (isValid) {
      console.log('✅ Validation passed, proceeding to Step 2');
      onNext();
    } else {
      console.log('❌ Validation failed, staying on Step 1');
      // Scroll to first error
      setTimeout(() => {
        const firstError = document.querySelector('.text-red-600');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  return (
    <div className="space-y-6">
      {/* Rule Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Rule Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name || ''}
          onChange={(e) => updateFormData({ name: e.target.value })}
          placeholder="e.g., Case Escalation for Urgent Issues"
          className={`
            w-full px-4 py-2 rounded-lg border
            ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
            bg-white dark:bg-gray-900 text-gray-900 dark:text-white
            focus:ring-2 focus:ring-blue-500 focus:border-transparent
          `}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.name}
          </p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea
          value={formData.description || ''}
          onChange={(e) => updateFormData({ description: e.target.value })}
          placeholder="Describe what this workflow rule does and when it should be used..."
          rows={3}
          className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Optional: Help other admins understand the purpose of this rule
        </p>
      </div>

      {/* Object Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Object <span className="text-red-500">*</span>
        </label>
        <select
          value={formData.objectApiName || ''}
          onChange={(e) => updateFormData({ objectApiName: e.target.value })}
          className={`
            w-full px-4 py-2 rounded-lg border
            ${errors.objectApiName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
            bg-white dark:bg-gray-900 text-gray-900 dark:text-white
            focus:ring-2 focus:ring-blue-500 focus:border-transparent
          `}
        >
          <option value="">Select an object...</option>
          {AVAILABLE_OBJECTS.map(obj => (
            <option key={obj} value={obj}>{obj}</option>
          ))}
        </select>
        {errors.objectApiName && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.objectApiName}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Select the object this rule applies to
        </p>
      </div>

      {/* Trigger Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          When should this rule run? <span className="text-red-500">*</span>
        </label>
        <div className="space-y-3">
          <label className="flex items-start gap-3 p-4 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            <input
              type="radio"
              name="triggerType"
              value="onCreateOnly"
              checked={formData.triggerType === 'onCreateOnly'}
              onChange={(e) => updateFormData({ triggerType: e.target.value as any })}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-white">
                Only when a record is created
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Rule triggers only when a new record is added
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 p-4 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            <input
              type="radio"
              name="triggerType"
              value="onCreateAndUpdate"
              checked={formData.triggerType === 'onCreateAndUpdate'}
              onChange={(e) => updateFormData({ triggerType: e.target.value as any })}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-white">
                When a record is created or updated
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Rule triggers when a record is created or any time it's modified
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 p-4 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            <input
              type="radio"
              name="triggerType"
              value="onUpdateOnly"
              checked={formData.triggerType === 'onUpdateOnly'}
              onChange={(e) => updateFormData({ triggerType: e.target.value as any })}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-white">
                Only when a record is updated
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Rule triggers only when an existing record is modified
              </div>
            </div>
          </label>
        </div>
        {errors.triggerType && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.triggerType}
          </p>
        )}
      </div>

      {/* Active Status */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isActive ?? true}
            onChange={(e) => updateFormData({ isActive: e.target.checked })}
            className="mt-0.5"
          />
          <div className="flex-1">
            <div className="font-medium text-gray-900 dark:text-white">
              Activate this rule immediately after saving
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              When active, this rule will automatically execute when conditions are met.
              Uncheck to save as inactive (you can activate it later).
            </div>
          </div>
        </label>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <div className="font-medium mb-1">Next Steps</div>
            <div>
              After defining the basic information, you'll configure the criteria (when should this rule trigger?)
              and actions (what should happen when it triggers?).
            </div>
          </div>
        </div>
      </div>

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <div className="text-sm text-red-800 dark:text-red-200">
              <div className="font-medium mb-1">Please fix the following errors:</div>
              <ul className="list-disc list-inside">
                {Object.values(errors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Hidden button to trigger validation on parent's Next click */}
      <button onClick={handleNext} style={{ display: 'none' }} id="step1-next-btn" />

      {/* Required Fields Note */}
      <div className="text-sm text-gray-500 dark:text-gray-400">
        <span className="text-red-500">*</span> Required fields
      </div>
    </div>
  );
};
